import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPinOff, MountainSnow } from "lucide-react";
import type { MapFeatureProperties } from "@atlas/shared";
import { AtlasMap } from "../components/map/AtlasMap";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { FeatureSummaryCard, type SelectedFeature } from "../components/FeatureSummaryCard";
import { fetchShowcase } from "../api/showcase";
import { computeFeatureCollectionBbox } from "../lib/geo";
import { LANDMARK_CATEGORIES } from "../lib/landmark-icons";
import { STORE_THEME_SKINS, applySiteTheme } from "../lib/theme";
import { usePageMeta } from "../hooks/usePageMeta";

const noop = () => {};

// Showcase — the public, HUD-free, full-screen map of everything an explorer
// has completed. Renders outside every app shell (bare root route); the only
// chrome is the attribution pill with the view's single gold CTA. Peaks render
// unclustered so the whole record is visible in one frame.
export default function ShowcasePage({ userId, sig }: { userId?: string; sig?: string }) {
  const hasLink = !!userId && !!sig;

  const showcaseQuery = useQuery({
    queryKey: ["share/showcase", userId],
    queryFn: () => fetchShowcase(userId!, sig!),
    enabled: hasLink,
    staleTime: 60_000,
    retry: 1,
  });
  const data = showcaseQuery.data;
  const ownerName = data?.owner.name ?? data?.owner.username ?? "An explorer";
  const [selected, setSelected] = useState<SelectedFeature | null>(null);
  // Clicking a collected pin (or trail line) opens the same summary card as
  // the activity share page - the detail endpoints behind it work logged out.
  const onFeatureSelect = useCallback((f: MapFeatureProperties) => {
    if (f.type !== "peak" && f.type !== "landmark" && f.type !== "trail") return;
    setSelected({ kind: f.type, slug: f.slug, name: f.name });
  }, []);

  usePageMeta({
    title: data ? `${ownerName}'s Atlas` : "Showcase",
    description: "A live map of every peak, trail and landmark this explorer has completed on Atlas.",
    canonicalPath: "/showcase",
  });

  // Outside AppShell nothing applies the owner's equipped site skin — mirror
  // AppShell's applySiteTheme call so the pill's tokens match their map theme.
  useEffect(() => {
    const skin = data?.equipped.mapTheme ? STORE_THEME_SKINS[data.equipped.mapTheme.slug] : null;
    applySiteTheme(skin ?? "dark");
  }, [data?.equipped.mapTheme]);

  // Frame to the curated collected record only. The heatmap is raw GPS traces,
  // where a single outlier coordinate (a glitch, null-island [0,0]) stretches
  // the union bbox across the globe and defeats the UK-focused fit; the peaks,
  // trails and landmarks come from clean DB geometry. With no completed record
  // the bbox is null and the map rests on its UK construction bounds.
  const fitBounds = useMemo(() => {
    if (!data) return null;
    return computeFeatureCollectionBbox(data.features);
  }, [data]);

  if (!hasLink || showcaseQuery.isError) {
    return (
      <div
        style={{
          height: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--bg-app)",
          padding: 20,
        }}
      >
        <EmptyState
          icon={<MapPinOff size={20} />}
          title="This showcase link isn't valid"
          hint="Ask the explorer who shared it for a fresh link."
        />
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "100vh", overflow: "hidden", background: "var(--bg-app)" }}>
      <AtlasMap
        features={data?.features ?? null}
        heatmap={data?.heatmap ?? null}
        selectedId={null}
        filter="completed"
        landmarkCategories={LANDMARK_CATEGORIES}
        onFeatureSelect={onFeatureSelect}
        onBoundsChange={noop}
        fitBounds={fitBounds}
        cluster={false}
        mapTheme={
          data?.equipped.mapTheme
            ? {
                heatRamp: data.equipped.mapTheme.metadata.heatRamp,
                traceColor: data.equipped.mapTheme.metadata.traceColor,
              }
            : null
        }
        traceStyle={
          data?.equipped.traceStyle
            ? {
                dashArray: data.equipped.traceStyle.metadata.dashArray,
                glow: data.equipped.traceStyle.metadata.glow,
              }
            : null
        }
        collectedPinColor={data?.equipped.pin?.metadata.color ?? null}
      />

      {data && selected && <FeatureSummaryCard selected={selected} onClose={() => setSelected(null)} />}

      {data && (
        <div
          className="atlas-glass"
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 8px 8px 14px",
            borderRadius: "var(--radius-pill)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <MountainSnow size={16} color="var(--gold-400)" />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>
            {ownerName}&#39;s Atlas
          </span>
          <Button size="sm" onClick={() => { window.location.href = "/signup"; }}>
            Join on Atlas
          </Button>
        </div>
      )}
    </div>
  );
}
